import { NotificationManager } from "react-notifications";
import * as Yup from "yup";
import { forEach, isFunction } from "lodash";

import PostServiceApi from "../../../../../../services/post-service";
import { formatDateTime } from "../../../../../../utils";
import FormMeta from "../form-meta/index.json";

const generateInitialFormValues = () =>
  Object.fromEntries(
    Object.values(FormMeta).map(({ id, initialValue }) => [id, initialValue]),
  );

const handleAcceptIdVerification = async ({
  values = {},
  requestId,
  refreshUserDetails = () => {},
  setShowIdVerificationModal = () => {},
}) => {
  const requestBody = {};

  requestBody.accept = true;
  requestBody[FormMeta.documentType.id] = values[FormMeta.documentType.id];
  requestBody[FormMeta.documentId.id] = values[FormMeta.documentId.id];
  requestBody[FormMeta.issuedDate.id] = formatDateTime(
    values[FormMeta.issuedDate.id],
  );
  requestBody[FormMeta.expiryDate.id] = formatDateTime(
    values[FormMeta.expiryDate.id],
  );
  requestBody[FormMeta.fullName.id] = values[FormMeta.fullName.id];
  requestBody[FormMeta.dateOfBirth.id] = formatDateTime(
    values[FormMeta.dateOfBirth.id],
  );

  try {
    const response = await PostServiceApi.acceptRejectIdVerificationRequest({
      data: requestBody,
      requestId,
    });

    if (!response.success) {
      NotificationManager.error("Failed to verify the user");
      throw new Error("Failed to verify the user");
    }

    if (isFunction(refreshUserDetails)) {
      await refreshUserDetails();
    }

    NotificationManager.success("User verified successfully");

    if (isFunction(setShowIdVerificationModal)) {
      setShowIdVerificationModal(false);
    }
  } catch (error) {
    throw new Error("form submission failed");
  }
};

const handleRejectionIdVerification = async ({
  requestId,
  reason,
  setShowIdVerificationRejectionModal = () => {},
  setShowIdVerificationModal = () => {},
  refreshUserDetails = () => {},
}) => {
  try {
    const requestBody = {
      accept: false,
    };

    if (reason) {
      requestBody.reason = reason;
    }

    const response = await PostServiceApi.acceptRejectIdVerificationRequest({
      requestId,
      data: requestBody,
    });

    if (!response.success) {
      NotificationManager.error("Failed to reject the verification request");
      throw new Error("Failed to verify user");
    }

    NotificationManager.success("Verification request rejected successfully");

    if (isFunction(refreshUserDetails)) {
      await refreshUserDetails();
    }
    if (isFunction(setShowIdVerificationRejectionModal)) {
      setShowIdVerificationRejectionModal(false);
    }
    if (isFunction(setShowIdVerificationModal)) {
      setShowIdVerificationModal(false);
    }
  } catch (error) {
    throw new Error("Unknown error occurred");
  }
};

const getIdVerificationRequestImages = async ({
  requestId,
  imageIds = [],
  setImages = () => {},
}) => {
  try {
    const tempImageUrls = [];
    for (const [index, imageId] of imageIds.entries()) {
      const response = await PostServiceApi.getIdVerificationRequestImage({
        requestId,
        imageId,
      });
      if (response.success) {
        const imageId = `id-image-${index}`;
        const imageUrl = URL.createObjectURL(response.body);
        const label = `Image ${index + 1}`;
        tempImageUrls.push({ imageId, imageUrl, label });
      }
    }
    setImages(tempImageUrls);
  } catch (error) {
    throw new Error("Unable to get images");
  }
};

export const IdVerificationDomain = Object.freeze({
  generateInitialFormValues,
  handleAcceptIdVerification,
  getIdVerificationRequestImages,
  handleRejectionIdVerification,
});
